import React, { useEffect, useMemo, useState } from "react";
import Modal from "Common/Components/Modal";
import {
  AlertTriangle,
  BadgeCheck,
  Clock,
  Loader,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { BankPayment, formatRial } from "Common/utils/bankPayment";

export type BankFlowStage =
  | "prepared"
  | "confirming"
  | "cancelling"
  | "result";

interface BankPaymentModalProps {
  show: boolean;
  payment: BankPayment | null;
  stage: BankFlowStage;
  isRefreshing?: boolean;
  onConfirm: () => void;
  onReject: () => void;
  onClose: () => void;
  onRefreshStatus: () => void;
  onRestartPreview: () => void;
}

const remainingSeconds = (expiresAt?: string | null): number | null => {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.floor((end - Date.now()) / 1000));
};

const BankPaymentModal = ({
  show,
  payment,
  stage,
  isRefreshing,
  onConfirm,
  onReject,
  onClose,
  onRefreshStatus,
  onRestartPreview,
}: BankPaymentModalProps) => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!show || stage !== "prepared") {
      setSecondsLeft(null);
      return;
    }
    setSecondsLeft(remainingSeconds(payment?.expires_at));
    const timer = setInterval(() => {
      setSecondsLeft(remainingSeconds(payment?.expires_at));
    }, 1000);
    return () => clearInterval(timer);
  }, [show, stage, payment?.expires_at]);

  const isExpired = secondsLeft !== null && secondsLeft <= 0;

  const countdownLabel = useMemo(() => {
    if (secondsLeft === null) return "";
    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }, [secondsLeft]);

  const confirmText =
    payment?.message ||
    `آیا از انتقال وجه ${formatRial(payment?.amount_rial)} ریال از حساب پیمانکار به حساب کشاورز ${payment?.recipient_name || ""} اطمینان دارید؟`;

  const needsReconciliation =
    payment?.status === "unknown" || payment?.requires_reconciliation === true;

  const renderResult = () => {
    if (!payment) return null;

    if (needsReconciliation) {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertTriangle className="text-amber-500" size={44} />
          <p className="font-medium dark:text-white">
            {payment.message ||
              "نتیجه پرداخت هنوز مشخص نیست. تا تعیین وضعیت، پرداخت را دوباره ثبت نکنید."}
          </p>
          <p className="text-sm text-slate-500 dark:text-zink-200">
            شناسه پیگیری پرداخت: <b>{payment.id}</b>
          </p>
          <p className="text-sm text-slate-500 dark:text-zink-200">
            برای بررسی نتیجه با پشتیبانی تماس بگیرید.
          </p>
        </div>
      );
    }

    if (payment.status === "succeeded") {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <BadgeCheck className="text-green-500" size={44} />
          <p className="font-medium dark:text-white">
            {payment.message ||
              `مبلغ ${formatRial(payment.amount_rial)} ریال به حساب کشاورز ${payment.recipient_name || ""} با موفقیت واریز شد.`}
          </p>
          {payment.bank_reference && (
            <p className="text-sm text-slate-500 dark:text-zink-200">
              شماره پیگیری بانکی: <b>{payment.bank_reference}</b>
            </p>
          )}
        </div>
      );
    }

    if (payment.status === "processing") {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader className="text-blue-500 animate-spin" size={44} />
          <p className="font-medium dark:text-white">
            {payment.message || "پرداخت در حال بررسی است"}
          </p>
          <p className="text-sm text-slate-500 dark:text-zink-200">
            تا مشخص شدن نتیجه، این پرداخت را دوباره ثبت نکنید.
          </p>
        </div>
      );
    }

    if (payment.status === "expired") {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <Clock className="text-amber-500" size={44} />
          <p className="font-medium dark:text-white">
            {payment.message ||
              "اعتبار تأیید پرداخت به پایان رسیده است. لطفاً مبلغ و نام گیرنده را دوباره تأیید کنید."}
          </p>
        </div>
      );
    }

    if (payment.status === "cancelled") {
      return (
        <div className="flex flex-col items-center gap-3 text-center">
          <XCircle className="text-slate-400" size={44} />
          <p className="font-medium dark:text-white">
            {payment.message || "پرداخت لغو شد."}
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <XCircle className="text-red-500" size={44} />
        <p className="font-medium dark:text-white">
          {payment.message || "پرداخت ناموفق بود."}
        </p>
        <p className="text-sm text-slate-500 dark:text-zink-200">
          برای تلاش دوباره، ابتدا علت را برطرف و سپس پرداخت جدیدی ثبت کنید.
        </p>
      </div>
    );
  };

  const showRetryPreview =
    stage === "result" &&
    (payment?.status === "expired" || payment?.status === "cancelled");

  const showStatusRefresh =
    stage === "result" &&
    (payment?.status === "processing" || needsReconciliation);

  return (
    <Modal
      show={show}
      onHide={onClose}
      className="fixed inset-0 flex items-center justify-center z-[9999] bg-opacity-gray-50 backdrop-blur-sm"
      dialogClassName="relative w-full max-w-md bg-white dark:bg-zink-600 rounded-lg shadow-xl mx-4 my-8"
    >
      <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500">
        <Modal.Title className="text-lg font-semibold">
          {stage === "prepared" ? "تأیید پرداخت بانکی" : "نتیجه پرداخت بانکی"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-6">
        {stage === "prepared" && (
          <div className="flex flex-col gap-4">
            <p className="leading-7 text-center dark:text-white">{confirmText}</p>

            <div className="p-3 rounded-md bg-slate-100 dark:bg-zink-500 dark:text-white flex flex-col gap-1 text-sm">
              <span>
                مبلغ: <b>{formatRial(payment?.amount_rial)} ریال</b>
              </span>
              <span>
                گیرنده: <b>{payment?.recipient_name || "-"}</b>
              </span>
              {secondsLeft !== null && (
                <span className={isExpired ? "text-red-500" : ""}>
                  {isExpired
                    ? "اعتبار تأیید به پایان رسیده است"
                    : `مهلت تأیید: ${countdownLabel}`}
                </span>
              )}
            </div>

            <div className="flex justify-center gap-3">
              {isExpired ? (
                <button
                  type="button"
                  onClick={onRestartPreview}
                  className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  دریافت پیش‌نمایش جدید
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onConfirm}
                  className="px-8 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  بلی
                </button>
              )}
              <button
                type="button"
                onClick={onReject}
                className="px-8 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600"
              >
                خیر
              </button>
            </div>
          </div>
        )}

        {stage === "confirming" && (
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader className="text-blue-500 animate-spin" size={44} />
            <p className="font-medium dark:text-white">در حال انجام پرداخت</p>
            <p className="text-sm text-slate-500 dark:text-zink-200">
              لطفاً تا مشخص شدن نتیجه صفحه را نبندید.
            </p>
          </div>
        )}

        {stage === "cancelling" && (
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader className="text-slate-500 animate-spin" size={44} />
            <p className="font-medium dark:text-white">در حال لغو پرداخت</p>
          </div>
        )}

        {stage === "result" && (
          <div className="flex flex-col gap-5">
            {renderResult()}
            <div className="flex justify-center gap-3">
              {showStatusRefresh && (
                <button
                  type="button"
                  disabled={isRefreshing}
                  onClick={onRefreshStatus}
                  className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <RefreshCw
                    size={16}
                    className={isRefreshing ? "animate-spin" : ""}
                  />
                  بررسی مجدد وضعیت
                </button>
              )}
              {showRetryPreview && (
                <button
                  type="button"
                  onClick={onRestartPreview}
                  className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  دریافت پیش‌نمایش جدید
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 border rounded-lg border-slate-300 dark:text-white dark:border-zink-400"
              >
                بستن
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BankPaymentModal;
